/**
 * Weekly proposal screen — the client's home.
 *
 * Layout:
 * - Context header: overline, H1, delivery date, state tag, cutoff countdown
 * - 2-column grid: product table (left) · sticky sidebar (right)
 * - Responsive: single column below 980px
 *
 * States handled:
 * - Loading: skeleton spinner
 * - Error: error message
 * - No proposal (order===null): friendly empty state
 * - Locked/read-only: state tag shown, no edit affordance
 * - Editable (EDITOR + before cutoff): live edit + save/discard
 *
 * The two halves are split: `useWeeklyProposal` reads (queries → view-model) and
 * `useProposalEditor` writes (local edit state, reconciliation, editLines). What
 * is left here is the rendering. The quantity floor is PER STORE
 * (`minQuantityPerProduct`): the selector never goes below it and a sub-floor
 * row is left out of the save payload. Above zero there is no opt-out
 * affordance; a store whose floor is 0 can zero any line — the classic opt-out.
 *
 * Per-store isolation: ProposalPage renders ProposalPageInner with key={storeId}
 * so switching the active store remounts it, resetting the editor's local state.
 * Without the key, edits made in one store would bleed into the next one selected.
 */
import React from 'react'
import type {CutoffRule} from '@retail/dates'
import {Button, CutoffCountdown, Tag} from '../../components'
import {useActiveStore} from '../../store/StoreContext'
import {useRegisterUnsavedChanges} from '../../layout/UnsavedChangesContext'
import {useWeeklyProposal} from './useWeeklyProposal'
import {useProposalEditor} from './useProposalEditor'
import {ProductTable} from './ProductTable'
import {ProposalSidebar} from './ProposalSidebar'
import {formatMadridDate} from '../../lib/dateFormat'
import {STATUS_BG, STATUS_LABELS} from '../../lib/orderStatus'
import styles from './ProposalPage.module.scss'

// ---------------------------------------------------------------------------
// Main component — resolves activeStore then delegates to inner
// ---------------------------------------------------------------------------

export const ProposalPage = (): React.ReactElement => {
  const {activeStore} = useActiveStore()

  if (activeStore === undefined) {
    return (
      <div className={styles['proposal-page']}>
        <div className={`row-flx flx-a-c flx-j-c p-24 txt-light-kale ${styles['proposal-page__loading']}`}>
          <span>Cargando tienda…</span>
        </div>
      </div>
    )
  }

  return (
    // key={storeId}: forces a remount of ProposalPageInner on store switch.
    // Without it, the same instance is reused across stores and the editor's
    // local state + baseline refs survive the switch, leaking one store's
    // unsaved edits into another's screen.
    <ProposalPageInner
      key={activeStore.id}
      storeId={activeStore.id}
      storeName={activeStore.name}
      role={activeStore.role}
      minQuantityPerProduct={activeStore.minQuantityPerProduct}
      cutoff={activeStore.cutoff}
    />
  )
}

type ProposalPageInnerProps = {
  storeId: number
  storeName: string
  role: 'VIEWER' | 'EDITOR'
  /** The store's per-line floor — the selector's min and the save filter's bound. */
  minQuantityPerProduct: number
  /** The store's own cutoff rule; absent = the general one. Decides the week this screen orders for. */
  cutoff?: CutoffRule
}

const ProposalPageInner = ({
  storeId,
  storeName,
  role,
  minQuantityPerProduct,
  cutoff,
}: ProposalPageInnerProps): React.ReactElement => {
  const viewModel = useWeeklyProposal({storeId, role, minQuantityPerProduct, cutoff})
  const editor = useProposalEditor({
    storeId,
    rows: viewModel.rows,
    order: viewModel.order,
    isEditable: viewModel.isEditable,
    minQuantityPerProduct,
  })

  // Publish dirty state to the shell-level guard (route nav + tab close) and to
  // the store switcher (which remounts this page without a route change). Only
  // an editable proposal can have unsaved edits worth guarding.
  useRegisterUnsavedChanges(editor.dirty && viewModel.isEditable)

  if (viewModel.isLoading) {
    return (
      <div className={styles['proposal-page']}>
        <div className={`row-flx flx-a-c flx-j-c p-24 txt-light-kale ${styles['proposal-page__loading']}`}>
          <span>Cargando propuesta…</span>
        </div>
      </div>
    )
  }

  if (viewModel.isError) {
    return (
      <div className={styles['proposal-page']}>
        <div className={`row-flx flx-a-c flx-j-c p-24 txt-a-c ${styles['proposal-page__error']}`}>
          <p>No se pudo cargar la propuesta. Comprueba tu conexión e inténtalo de nuevo.</p>
        </div>
      </div>
    )
  }

  const {isoYear, isoWeek} = viewModel.week
  // The deadline card is relevant to everyone before the cutoff (viewers too),
  // so it keys off isLocked (status), not edit permission.
  const showCountdown = !viewModel.isLocked && viewModel.cutoffAt !== null

  // Local edits outlive the editor: the cutoff can pass with the screen open
  // (see useWeeklyProposal), and the "server changed" notice would then lose
  // both its explanation and its only exit if this block were gated on
  // isEditable. Discarding stays available so the user can always get back to
  // the real order.
  const showDiscard = editor.dirty || editor.serverChangedWhileDirty
  // The out-of-date proposal opens the actions too: with nothing edited there is
  // no dirty flag, and the save is exactly what the store needs to reach. Only
  // for someone who CAN save — a VIEWER would get an empty actions block and a
  // notice naming an action it has no button for.
  const showOutOfDate = editor.cartaChanged && viewModel.isEditable
  const showActions = viewModel.isEditable || showDiscard || editor.mutationError !== null

  return (
    <div className={styles['proposal-page']}>
      {/* The unsaved-changes guard now lives at the shell (AppShell) so it also
          covers store switches — see useRegisterUnsavedChanges above. */}
      <div className={`m-v-0 m-h-auto ${styles['proposal-page__wrap']}`}>
        {/* Context header */}
        <div className={`row-flx flx-a-e flx-j-b flx-w ${styles['proposal-page__ctx']}`}>
          <div className={styles['proposal-page__lede']}>
            <div className={`m-b-4 txt-bold-500 txt-upper txt-light-kale ${styles['proposal-page__overline']}`}>
              Tu pedido · semana {isoYear} / {isoWeek.toString().padStart(2, '0')}
            </div>
            <h1 className={`m-h-0 m-b-0 txt-honey txt-kale ${styles['proposal-page__title']}`}>Propuesta de pedido</h1>
            {viewModel.delivery !== null && (
              <div className={`txt-light-kale ${styles['proposal-page__delivery-date']}`}>
                {`Entrega ${formatMadridDate(viewModel.delivery.option.deliveryDate)} · ${viewModel.delivery.option.start}–${viewModel.delivery.option.end}`}
              </div>
            )}
            <div className={`txt-light-kale ${styles['proposal-page__store-name']}`}>{storeName}</div>
          </div>

          <div className="row-flx flx-a-c flx-gap-12 flx-w">
            {viewModel.status !== null && (
              <Tag
                label={STATUS_LABELS[viewModel.status]}
                bgColor={STATUS_BG[viewModel.status]}
              />
            )}
          </div>
        </div>

        {/* No-proposal empty state */}
        {viewModel.order === null ? (
          <div className={`p-v-40 p-h-24 txt-a-c bg-white txt-kale ${styles['proposal-page__no-proposal']}`}>
            <p>Todavía no hay propuesta generada para esta semana.</p>
            <p className={`txt-light-kale ${styles['proposal-page__no-proposal-sub']}`}>
              La propuesta se genera automáticamente antes del cierre. Vuelve más tarde.
            </p>
          </div>
        ) : (
          /*
           * Four orderable slots. Desktop: table (left) with countdown / actions /
           * summary stacked in the right rail. Mobile: single column reordered to
           * countdown → actions (save on top) → table → summary.
           */
          <div className={styles['proposal-page__layout']}>
            {showCountdown && (
              <div className={styles['proposal-page__countdown']}>
                <CutoffCountdown cutoffAt={viewModel.cutoffAt as Date} />
              </div>
            )}

            {showActions && (
              <div className={`col-flx ${styles['proposal-page__actions']}`}>
                {(viewModel.isEditable || showDiscard) && (
                  <div className={`col-flx ${styles['proposal-page__actions-btns']}`}>
                    {viewModel.isEditable && (
                      <Button
                        variant="pill"
                        type="button"
                        bgColor="olive"
                        txtColor="white"
                        className={`full-width ${styles['proposal-page__save']}`}
                        onClick={editor.onSave}
                        disabled={editor.isSaving || !(editor.dirty || showOutOfDate)}
                        aria-busy={editor.isSaving}
                        label={editor.isSaving ? 'Guardando…' : 'Guardar cambios'}
                      />
                    )}
                    {showDiscard && (
                      <Button
                        variant="textual"
                        type="button"
                        txtColor="light-kale"
                        className={`full-width ${styles['proposal-page__discard']}`}
                        onClick={editor.onDiscard}
                        disabled={editor.isSaving}
                        label="Descartar cambios"
                      />
                    )}
                  </div>
                )}

                {/* The copy does not blame the carta: the same divergence
                    appears when staff left a line below the store's floor. */}
                {showOutOfDate && (
                  <div className={`border-s bg-light-corn ${styles['proposal-page__server-notice']}`} role="status">
                    Tu pedido no está al día con la carta. Guarda para actualizarlo.
                  </div>
                )}

                {editor.serverChangedWhileDirty && (
                  <div className={`border-s bg-light-corn ${styles['proposal-page__server-notice']}`} role="status">
                    El pedido ha cambiado en el servidor. Descarta tus cambios para ver la versión actualizada.
                  </div>
                )}

                {editor.mutationError !== null && (
                  <div className={`border-s bg-pale-grana txt-grana ${styles['proposal-page__mutation-error']}`} role="alert">
                    {editor.mutationError}
                  </div>
                )}
              </div>
            )}

            <div className={styles['proposal-page__table']}>
              <ProductTable
                rows={viewModel.rows}
                localQuantities={editor.localQuantities}
                isEditable={viewModel.isEditable}
                isSaving={editor.isSaving}
                onQuantityChange={editor.onQuantityChange}
                minQuantityPerProduct={minQuantityPerProduct}
              />
            </div>

            <div className={styles['proposal-page__summary']}>
              <ProposalSidebar
                viewModel={viewModel}
                localTotalUnits={editor.localTotalUnits}
                localTotalRefs={editor.localTotalRefs}
                minQuantityPerProduct={minQuantityPerProduct}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
